import React, { useState, useEffect } from "react";
import { createGlobalStyle } from "styled-components";
import Select from "react-select";

import adm2_ from "./static/data/admin2_complete.json";
import adm3_ from "./static/data/admin3_complete.json";

import Sidebar from "./Sidebar";
import Legend from "./Legend";
import Credits from "./Credits";
import EditButton from "./EditButton";

function compare(a, b) {
  // Use toUpperCase() to ignore character casing
  const areaA = a.label.toUpperCase();
  const areaB = b.label.toUpperCase();

  let comparison = 0;
  if (areaA > areaB) {
    comparison = 1;
  } else if (areaA < areaB) {
    comparison = -1;
  }
  return comparison;
}

const admin2 = adm2_.features.map((d) => d.properties).sort(compare);
const admin3 = adm3_.features.map((d) => d.properties).sort(compare);

const GlobalStyle = createGlobalStyle`
  body {
    font-family: aktiv-grotesk, sans-serif;
    color: white;
  }

  a {
    color: inherit;
  }

  .area-select{
    width: 200px;
    color: #000;
    font-size: 10px;
    margin: 20px 0px 0px 20px;
    display: flex;
    flex-direction: column
  }

  .area-select > div{
    margin: 5px 0px 5px 0px;
  }

  .ddAdmin2{
    z-index: 5;
  }

  .ddAdmin3{
    z-index: 4;
  }

  .ddAdmin2 > div, .ddAdmin3 > div{
    border-style: none;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
  }

  .backToTop{
    z-index: 2;
    width: 55%;
    height: 30px;
    border-radius: 5px;
    border: none;
    margin-top: 5px;
    margin-bottom: 5px;
    font-weight: 500;
    font-size: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
  }

  .selectYear{
    z-index: 3;
    background-color: white;
    width: 55%;
    height: 45px;
    justify-content: center;
    align-items: left;
    border-radius: 5px;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
  }

  .radio {
    margin-left: 10px
  }

  div.mapboxgl-ctrl.mapboxgl-ctrl-group{
    visibility: hidden
  }

  .sidebar-content{
    margin: 20px 20px 0px 0px
    padding: 20px
    width: 40rem
    height: 20.2rem
    display: flex;
    color: #fff
    flex-direction: row;
    text-align: start;
    background-color: #00000095;
    border-radius: 2px
    font-family: aktiv-grotesk;
  }
  .sidebar-body {
    font-size: 10px;
    font-weight: 400;
    margin-bottom: 10px
    margin-top: 0px
  }

  .sidebar2021{
    width: 50%
    display: inline-block
    margin-top: -10px;
  }

  .sidebar2020{
    width: 50%
    display: inline-block
    margin-top: -10px;
  }

  .perc-comp{
    font-size: 48px;
    font-weight: 700
    margin-bottom: 15px
  }

  .population-container{
    display: flex;
    flex-direction: row
  }

  .population{
    font-size: 24px;
    font-weight: 700
    margin-bottom: 63px
    margin-left: 10px
  }

  .value-title{
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 10px
    margin-top: 0px
  }

  .sidebar-content > h1{
    margin: 0px 0px 20px 0px
  }
`;

const App = ({ map, controls }) => {
  const [district, setDistrict] = useState(null);
  const [commune, setCommune] = useState(null);
  const [year, setYear] = useState(" ");
  const [communeList, setCommunesList] = useState([]);

  const [sidebar, setSidebar] = useState({
    agg: "country",
    data: {
      _popsum: 26696210,
      percentage_completeness_jan2020: 10.89,
      percentage_completeness_november2021: 20.46,
    },
  });

  const [state, setState] = useState({
    ...map.getCenter(),
    zoom: parseInt(map.getZoom(), 10),
    showAbout: true,
  });

  useEffect(() => {
    map.addSource("outline-adm2", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });

    map.addLayer({
      id: "outline-adm2",
      type: "line",
      source: "outline-adm2",
      paint: {
        "line-color": "#fff",
        "line-width": 2,
      },
    });

    map.addSource("outline-adm3", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });

    map.addLayer({
      id: "outline-adm3",
      type: "line",
      source: "outline-adm3",
      paint: {
        "line-color": "#fff",
        "line-width": 2,
      },
    });
  }, []);

  function toggleAbout(e) {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      showAbout: !prevState.showAbout,
    }));
  }

  useEffect(() => {
    function update (data) {
      setState(prevState => ({
        ...prevState,
        ...map.getCenter(),
        zoom: parseInt(map.getZoom(), 10)
      }))
    }
    map.on('moveend', update)
    map.on('zoomend', update)
    return function cleanup () {
      map.off('moveend', update)
      map.off('zoomend', update)
    }
  })

  function changeDistrict(selectedDistrict) {
    setDistrict(selectedDistrict);

    map.getSource("outline-adm2").setData({
      type: "FeatureCollection",
      features: adm2_.features.filter(
        (d) => d.properties.value === selectedDistrict.value
      ),
    });

    map.getSource("outline-adm3").setData({
      type: "FeatureCollection",
      features: [],
    });

    setCommune(null);

    map.fitBounds([
      [selectedDistrict.minx, selectedDistrict.miny],
      [selectedDistrict.maxx, selectedDistrict.maxy],
    ]);
  }

  useEffect(() => {
    if (!district) {
      return;
    }
    setCommunesList(admin3.filter((d) => d.ADM2_PCODE === district["value"]));
  }, [district]);

  function changeCommune(selectedCommune) {
    setCommune(selectedCommune);

    map.getSource("outline-adm2").setData({
      type: "FeatureCollection",
      features: [],
    });

    map.getSource("outline-adm3").setData({
      type: "FeatureCollection",
      features: adm3_.features.filter(
        (d) => d.properties.value === selectedCommune.value
      ),
    });

    map.fitBounds([
      [selectedCommune.minx, selectedCommune.miny],
      [selectedCommune.maxx, selectedCommune.maxy],
    ]);
  }

  function backToTop() {
    setCommune(null);
    setDistrict(null);

    map.getSource("outline-adm2").setData({
      type: "FeatureCollection",
      features: [],
    });

    map.getSource("outline-adm3").setData({
      type: "FeatureCollection",
      features: [],
    });

    map.flyTo({
      center: [47.259, -18.409],
      zoom: 5,
    });
  }

  function toggle2021Layer() {
    map.setLayoutProperty("level2_district_2021", "visibility", "visible");
    map.setLayoutProperty("level3_commune_2021", "visibility", "visible");
    map.setLayoutProperty("osm-does-not-exist-2021", "visibility", "visible");
    map.setLayoutProperty("osm-exists-2021", "visibility", "visible");

    map.setLayoutProperty("level2_district", "visibility", "none");
    map.setLayoutProperty("level3_commune", "visibility", "none");
    map.setLayoutProperty("osm-does-not-exist", "visibility", "none");
    map.setLayoutProperty("osm-exists", "visibility", "none");
  }

  function toggle2020Layer() {
    map.setLayoutProperty("level2_district_2021", "visibility", "none");
    map.setLayoutProperty("level3_commune_2021", "visibility", "none");
    map.setLayoutProperty("osm-does-not-exist-2021", "visibility", "none");
    map.setLayoutProperty("osm-exists-2021", "visibility", "none");

    map.setLayoutProperty("level2_district", "visibility", "visible");
    map.setLayoutProperty("level3_commune", "visibility", "visible");
    map.setLayoutProperty("osm-does-not-exist", "visibility", "visible");
    map.setLayoutProperty("osm-exists", "visibility", "visible");
  }

  const radioHandler = (year) => {
    setYear(year);
    if (year === "2021") {
      toggle2021Layer();
    } else if (year === "2020") {
      toggle2020Layer();
    }
  };

  useEffect(() => {
    if (!commune) {
      if (!district) {
        setSidebar({
          agg: "country",
          data: {
            _popsum: 26696210,
            percentage_completeness_jan2020: 10.89,
            percentage_completeness_november2021: 20.46,
          },
        });
      } else {
        setSidebar({
          agg: "district",
          data: district,
        });
      }
    } else {
      setSidebar({
        agg: "commune",
        data: commune,
      }); 
    }
  }, [commune, district]);

  return (
    <React.Fragment>
      <GlobalStyle />
      <Sidebar
        el={controls["top-right"].el}
        state={state}
        toggleAbout={toggleAbout}
        data={sidebar}
      />
      <div className="area-select">
        <Select
          options={admin2}
          onChange={(d) => changeDistrict(d)}
          placeholder={"Select District..."}
          value={district}
          className={"ddAdmin2"}
        />
        <Select
          options={communeList}
          onChange={(d) => changeCommune(d)}
          isDisabled={!district}
          placeholder={"Select Commune..."}
          value={commune}
          className={"ddAdmin3"}
        />
        <button
          type="submit"
          className={"backToTop"}
          onClick={() => backToTop()}
        >
          <b>Back to top</b>
        </button>
        <div className="selectYear">
          <div>
            <input
              type="radio"
              value="2021"
              name="year"
              className="radio"
              defaultChecked
              onClick={(e) => radioHandler("2021")}
            />{" "}
            2021 data
          </div>
          <div>
            <input
              type="radio"
              value="2020"
              name="year"
              className="radio"
              checked={year === "2020"}
              onClick={(e) => radioHandler("2020")}
            />{" "}
            2020 data
          </div>
        </div>
      </div>
      <Legend el={controls["bottom-left"].el} state={state} />
      <Credits el={controls["bottom-right"].el} />
      <EditButton state={state} />
    </React.Fragment>
  );
};

export default App;
